import { Link as RouterLink } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Paper,
  Divider,
  LinearProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  Lightbulb as HintIcon,
  ExpandMore as ExpandIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import {
  getPracticeQuestions,
  submitPracticeAnswer,
  getStudentPracticeProgress,
} from '../../services/lessonService';
import { RichTextEditor } from '../shared/RichTextEditor';

interface PracticeQuestionViewerProps {
  moduleId: string;
  unitId: string;
  unitTitle: string;
}

export const PracticeQuestionViewer: React.FC<PracticeQuestionViewerProps> = ({
  moduleId,
  unitId,
  unitTitle,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [practiceSet, setPracticeSet] = useState<any>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [feedback, setFeedback] = useState<Record<string, any>>({});
  const [hintsUsed, setHintsUsed] = useState<Record<string, number>>({});
  const [showHints, setShowHints] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<any>(null);

  useEffect(() => {
    loadPracticeQuestions();
    loadProgress();
  }, [moduleId, unitId]);

  const loadPracticeQuestions = async () => {
    try {
      setLoading(true);
      const result = await getPracticeQuestions(moduleId, unitId);

      console.log('Practice questions API response:', result);

      // The API returns practice_questions as an object, not an array
      if (result.practice_questions) {
        setPracticeSet(result.practice_questions);
      }
    } catch (err: any) {
      console.error('Error loading practice questions:', err);
      setError(err.message || 'Failed to load practice questions');
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const result = await getStudentPracticeProgress(moduleId, unitId);
      setProgress(result.progress);
    } catch (err) {
      console.error('Failed to load progress:', err);
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers({
      ...answers,
      [questionId]: value,
    });
  };

  const handleSubmitAnswer = async (question: any) => {
    if (!practiceSet) return;

    const answer = answers[question.question_id];
    if (answer === undefined || answer === '') {
      setError('Please provide an answer before submitting');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const result = await submitPracticeAnswer(
        moduleId,
        unitId,
        practiceSet.set_id,
        question.question_id,
        answer,
        undefined, // timeTakenSeconds - optional
      );

      setFeedback({
        ...feedback,
        [question.question_id]: result,
      });

      // Reload progress
      await loadProgress();
    } catch (err: any) {
      setError(err.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseHint = (questionId: string) => {
    const currentHints = hintsUsed[questionId] || 0;
    setHintsUsed({
      ...hintsUsed,
      [questionId]: currentHints + 1,
    });
    setShowHints({
      ...showHints,
      [questionId]: true,
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < practiceSet.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowHints({});
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowHints({});
    }
  };

  const renderQuestionInput = (question: any) => {
    const questionId = question.question_id;
    const answer = answers[questionId];
    const questionFeedback = feedback[questionId];
    const isAnswered = questionFeedback !== undefined;

    switch (question.question_type) {
      case 'multiple_choice':
        return (
          <FormControl component="fieldset" fullWidth disabled={isAnswered}>
            <RadioGroup
              value={answer || ''}
              onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            >
              {question.options?.map((option: any, idx: number) => (
                <Paper
                  key={idx}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    border: 2,
                    borderColor:
                      isAnswered && option.text === questionFeedback.correct_answer
                        ? 'success.main'
                        : answer === option.text
                        ? 'primary.main'
                        : 'grey.300',
                    bgcolor:
                      isAnswered && option.text === questionFeedback.correct_answer
                        ? 'success.50'
                        : 'transparent',
                  }}
                >
                  <FormControlLabel value={option.text} control={<Radio />} label={option.text} />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'true_false':
        return (
          <FormControl component="fieldset" fullWidth disabled={isAnswered}>
            <RadioGroup
              value={answer !== undefined ? String(answer) : ''}
              onChange={(e) => handleAnswerChange(questionId, e.target.value === 'true')}
            >
              {['true', 'false'].map((option) => (
                <Paper
                  key={option}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    border: 2,
                    borderColor:
                      isAnswered && String(questionFeedback.correct_answer) === option
                        ? 'success.main'
                        : String(answer) === option
                        ? 'primary.main'
                        : 'grey.300',
                    bgcolor:
                      isAnswered && String(questionFeedback.correct_answer) === option
                        ? 'success.50'
                        : 'transparent',
                  }}
                >
                  <FormControlLabel
                    value={option}
                    control={<Radio />}
                    label={option === 'true' ? 'True' : 'False'}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'numeric':
      case 'short_answer':
      case 'word_problem':
        return (
          <TextField
            fullWidth
            multiline={question.question_type === 'word_problem'}
            rows={question.question_type === 'word_problem' ? 4 : 1}
            value={answer || ''}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            disabled={isAnswered}
            placeholder={
              question.question_type === 'numeric'
                ? 'Enter your numeric answer'
                : 'Enter your answer'
            }
            type={question.question_type === 'numeric' ? 'number' : 'text'}
            sx={{ mb: 2 }}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!practiceSet || !practiceSet.questions || practiceSet.questions.length === 0) {
    return (
      <Alert severity="info">
        No practice questions available for this unit yet. Your teacher may add them soon!
      </Alert>
    );
  }

  const currentQuestion = practiceSet.questions[currentQuestionIndex];
  const questionFeedback = feedback[currentQuestion.question_id];
  const isAnswered = questionFeedback !== undefined;
  const currentHintsUsed = hintsUsed[currentQuestion.question_id] || 0;
  const availableHints = currentQuestion.hints || [];
  const canUseHint = currentHintsUsed < availableHints.length && !isAnswered;
  const savedProgress = Array.isArray(progress)
    ? progress.find((item) => item.set_id === practiceSet.set_id) || progress[0]
    : progress;
  const savedCorrectCount = savedProgress?.questions_correct?.length;
  const savedAttemptedCount = savedProgress?.questions_attempted?.length;

  const completedCount =
    savedCorrectCount ?? Object.keys(feedback).filter((qId) => feedback[qId].is_correct).length;
  const attemptedCount =
    savedAttemptedCount ??
    Object.keys(feedback).filter((qId) => feedback[qId] !== undefined).length;
  const totalQuestions = practiceSet.questions.length;
  const progressPercent = (completedCount / totalQuestions) * 100;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <QuizIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {practiceSet.title}
          </Typography>
        </Box>
        {practiceSet.description && (
          <Typography variant="body2" color="text.secondary">
            {practiceSet.description}
          </Typography>
        )}
      </Box>

      {/* Progress Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {completedCount} / {totalQuestions} correct
              {attemptedCount > completedCount ? `, ${attemptedCount} attempted` : ''}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current Question */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Question Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={`Question ${currentQuestionIndex + 1} of ${totalQuestions}`}
                color="primary"
                size="small"
              />
              <Chip label={currentQuestion.difficulty} size="small" variant="outlined" />
              <Chip label={currentQuestion.question_type.replace('_', ' ')} size="small" />
              <Chip label={`${currentQuestion.points} pts`} size="small" color="secondary" />
            </Box>
          </Box>

          {/* Question Text */}
          <Box
            sx={{
              mb: 3,
              '& > .MuiPaper-root': { border: 0 },
              '& > .MuiPaper-root > .MuiBox-root': { p: 0 },
              '& .ProseMirror': {
                fontSize: '1.25rem',
                fontWeight: 500,
              },
            }}
          >
            <RichTextEditor
              content={currentQuestion.question_text}
              onChange={() => undefined}
              readOnly
              minHeight={0}
            />
          </Box>

          {/* Answer Input */}
          {renderQuestionInput(currentQuestion)}

          {/* Hints Section */}
          {availableHints.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<HintIcon />}
                onClick={() => handleUseHint(currentQuestion.question_id)}
                disabled={!canUseHint}
                size="small"
                variant="outlined"
              >
                {isAnswered
                  ? 'Question Answered'
                  : currentHintsUsed >= availableHints.length
                  ? 'No More Hints'
                  : `Use Hint (${currentHintsUsed}/${availableHints.length} used)`}
              </Button>

              <Collapse in={showHints[currentQuestion.question_id]}>
                <Box sx={{ mt: 2 }}>
                  {availableHints.slice(0, currentHintsUsed).map((hint: string, idx: number) => (
                    <Alert key={idx} severity="info" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Hint {idx + 1}:</strong> {hint}
                      </Typography>
                    </Alert>
                  ))}
                  {currentHintsUsed > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Note: Using hints reduces your points by 20% per hint
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Feedback */}
          {isAnswered && questionFeedback.feedback_status && (
            <Alert severity="info" sx={{ mt: 3 }}>
              Your answer is saved. Feedback will be available after your teacher reviews it.
              <Button component={RouterLink} to="/student/feedback">
                View my feedback
              </Button>
            </Alert>
          )}
          {isAnswered && !questionFeedback.feedback_status && (
            <Alert
              severity={questionFeedback.is_correct ? 'success' : 'error'}
              icon={questionFeedback.is_correct ? <CheckIcon /> : <WrongIcon />}
              sx={{ mt: 3 }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {questionFeedback.is_correct ? 'Correct!' : 'Incorrect'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Points earned: {questionFeedback.points_earned ?? 0} / {currentQuestion.points ?? 1}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                <strong>Explanation:</strong> {questionFeedback.explanation}
              </Typography>
              {!questionFeedback.is_correct && questionFeedback.correct_answer && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Correct Answer:</strong> {String(questionFeedback.correct_answer)}
                </Typography>
              )}
            </Alert>
          )}

          {/* Submit Button */}
          {!isAnswered && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleSubmitAnswer(currentQuestion)}
                disabled={
                  submitting ||
                  answers[currentQuestion.question_id] === undefined ||
                  answers[currentQuestion.question_id] === ''
                }
                sx={{ px: 6 }}
              >
                {submitting ? 'Submitting...' : 'Submit Answer'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={handlePrevQuestion}
          disabled={currentQuestionIndex === 0}
        >
          Previous Question
        </Button>
        <Button
          variant="outlined"
          onClick={handleNextQuestion}
          disabled={currentQuestionIndex === totalQuestions - 1}
        >
          Next Question
        </Button>
      </Box>
    </Box>
  );
};
